import type { Nullable } from '@iheartjane/types';
import max from 'lodash-es/max';
import min from 'lodash-es/min';

import type {
  AlgoliaFacet,
  FilterGroup,
  FilterRangeItem,
  FilterSelectItem,
} from '@jane/search/types';

interface BuildPotencyRangeFilterProps {
  cannabinoid: 'thc' | 'cbd';
  percentages: number[];
}

interface BuildFilterRangeItems {
  cbdPercentages: number[];
  thcPercentages: number[];
}

export const buildPotencyFilterRangeItem = ({
  percentages,
  cannabinoid,
}: BuildPotencyRangeFilterProps): FilterRangeItem | null => {
  const potencyMin = min(percentages) ?? 0;
  const potencyMax = max(percentages) ?? 0;

  if (potencyMin === 0 && potencyMax === 0) {
    return null;
  } else {
    return {
      key: `percent_${cannabinoid}`,
      label: `${cannabinoid.toUpperCase()} Potency`,
      max: potencyMax,
      min: potencyMin,
    };
  }
};

export const isFilterRangeItem = (
  item: FilterRangeItem | null
): item is FilterRangeItem => item !== null;

export const isFilterGroup = (
  filterGroup: Nullable<FilterGroup>
): filterGroup is FilterGroup => filterGroup !== null;

export const buildFilterRangeItems = ({
  thcPercentages,
  cbdPercentages,
}: BuildFilterRangeItems): FilterRangeItem[] | null => {
  const thcFilterRangeItem = buildPotencyFilterRangeItem({
    cannabinoid: 'thc',
    percentages: thcPercentages,
  });

  const cbdFilterRangeItem = buildPotencyFilterRangeItem({
    cannabinoid: 'cbd',
    percentages: cbdPercentages,
  });

  if (!thcFilterRangeItem && !cbdFilterRangeItem) {
    return null;
  } else {
    return [thcFilterRangeItem, cbdFilterRangeItem].filter<FilterRangeItem>(
      isFilterRangeItem
    );
  }
};

export const validateRatingFilterGroup = (group: FilterSelectItem[]) =>
  group.filter((item) => item.count !== 0).length !== 0;

export const hasFacetCounts = (facet: AlgoliaFacet = {}): boolean => {
  return Object.values(facet).some((count) => count > 0);
};
